import moment from 'moment';

import { FoundationUtils } from '../../../../utils/foundation-utils';
import { ADEP_E1113Parameters, FunctionName } from '../../../../utils/parameters';
import { YesNoType } from '../../../../utils/enum-utils';
import { BooleanUtils } from '../../../../utils/boolean-utils';
import { Function1113Param13Values } from '../oral-supplements-viewer/oral-supplements-params';
import { PrescriptionType } from '../../services/timeline-service';
import MedicationService, {
  ConsistAdministrationMarginInfoRequest,
  ConsistAdministrationMarginInfoResponse
} from '../../services/medication-service';
import { ISO_FULL_DATE_FORMAT } from '../../../../constants/date-utils';

export interface ConsistAdministrationMarginInfoParams {
  administrationDate: string;
  itemType: PrescriptionType;
  justification?: string;
  scheduleSequenceNumber: string | number;
}

export type MarginValidationResult =
  | { allowed: true }
  | { allowed: false; message: string };

export class ConsistAdministrationMarginInfo {

  public static isMarginValidationEnabled(): boolean {
    const marginAction = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ACTION_TO_BE_TAKEN_IN_CASE_OF_EXTRAPOLATION_OF_THE_CHECKING_MARGIN);
    return marginAction === Function1113Param13Values.NOT_ALLOWED
      || marginAction === Function1113Param13Values.REQUIRED_JUSTIFICATION
      || marginAction === Function1113Param13Values.NOT_ALLOWED_AND_jUSTIFICATION;
  }

  public static async validate(params: ConsistAdministrationMarginInfoParams): Promise<MarginValidationResult> {
    if (!this.isMarginValidationEnabled()) {
      return { allowed: true };
    }
    try {
      const response = await this.execute(params);
      if (response.responseDescription) {
        return { allowed: false, message: response.responseDescription.replace(/#*/, '') };
      }
      return { allowed: true };
    } catch (err: any) {
      const rawMessage: string | undefined = err?.body?.errors?.[0] ?? err?.body?.message;
      return {
        allowed: false,
        message: rawMessage ? rawMessage.replace(/#*/, '') : 'tws.generic-backend-error.call-administrator',
      };
    }
  }

  public static execute(params: ConsistAdministrationMarginInfoParams): Promise<ConsistAdministrationMarginInfoResponse> {
    const { justification, scheduleSequenceNumber, itemType } = params;
    const dateTimeCurrent = moment(params.administrationDate).format(ISO_FULL_DATE_FORMAT);
    const param13 = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ACTION_TO_BE_TAKEN_IN_CASE_OF_EXTRAPOLATION_OF_THE_CHECKING_MARGIN) as string;
    const param507 = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ACTION_IN_CASE_EXTRAPOLATION_CHECKING_MARGIN_PRIOR_START) as number | undefined;
    const param12 = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.MARGIN_IN_MINUTES_CHECKING_OUTSIDE_PRESCRIBED_HOURS) as number;
    const justificationRequired = param13 === Function1113Param13Values.REQUIRED_JUSTIFICATION || param13 === Function1113Param13Values.NOT_ALLOWED_AND_jUSTIFICATION;
    const requiresJustificationStatus = justificationRequired ? YesNoType.YES : YesNoType.NO;

    const body: ConsistAdministrationMarginInfoRequest = {
      actionAdministrationOutsideMarginStatus: param13,
      eventDate: dateTimeCurrent,
      hasAsEvent: BooleanUtils.isPositiveValue(!!justification),
      itemType: itemType,
      justificationDescription: justification ? String(justification) : '',
      minutesMarginBefore: param507 ?? 0,
      minutesQuantityMarginAdministration: param12,
      requiresJustificationStatus: requiresJustificationStatus,
      scheduleSequence: Number(scheduleSequenceNumber),
    };

    return MedicationService.consistAdministrationMarginInfoResponse(body);
  }
}
